import { useState, useEffect } from "react";
import waterMonitoringSvc from "../../../services/api/waterMonitoring";
import { WaterMonitoringChartRequest, WaterMonitoringChartItem } from "../../../models/types/waterMonitoring";
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useResponsive } from '../../../hooks/mediaQuery';
import RenderChart from './render-chart';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const wmTypeOptions = [
    { value: 'day', label: 'Trung bình ngày' },
    { value: 'hour', label: 'Trung bình giờ' },
];

export default function DashboardChart({ stationId }: { stationId: number }) {
    const { t } = useTranslation();
    const [chartType, setChartType] = useState<'line' | 'bar'>('bar');
    const [visibleParams, setVisibleParams] = useState<string[]>([]);
    const { isXs } = useResponsive();

    const [waterMonitoring, setWaterMonitoring] = useState<WaterMonitoringChartItem[] | null>([]);
    const today = dayjs();
    const [wMParams, setWMParams] = useState<WaterMonitoringChartRequest>({
        stationId: stationId,
        type: 'day',
        from: today.subtract(7, 'day').format('MM-DD-YYYY'),
        to: today.add(1439, 'minute').format('MM-DD-YYYY HH:mm')
    });

    const getWaterMonitoring = async (params: WaterMonitoringChartRequest) => {
        try {
            const res = await waterMonitoringSvc.getWaterMonitoringForChart(params);
            if (res && res.content) {
                setWaterMonitoring(res.content);
            }
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        getWaterMonitoring(wMParams)
    }, [wMParams])

    const handleChangeWMParams = (data: Partial<WaterMonitoringChartRequest>) => {
        setWMParams({ ...wMParams, ...data });
    };

    const handleDateChange = (params: any) => {
        if (params[0] && params[1]) {
            setWMParams({ ...wMParams, from: dayjs(params[0]).format('YYYY-MM-DD HH:mm'), to: dayjs(params[1]).add(1439, 'minute').format('YYYY-MM-DD HH:mm') });
        }
    }

    return (
        <div>
            <div className="flex flex-wrap justify-between gap-2 items-start mb-4">
                <button
                    className="px-4 py-2 bg-blue-500 text-white rounded-md"
                    onClick={() => setChartType(chartType === 'line' ? 'bar' : 'line')}
                >
                    Dạng {chartType === 'line' ? 'cột' : 'đường'}
                </button>
                <div className={`flex ${isXs ? 'flex-1' : ''} flex-wrap gap-2 items-center`}>
                    <div className="min-w-[228px]">
                        <Select
                            options={wmTypeOptions}
                            value={wmTypeOptions.find(option => option.value === wMParams.type)}
                            onChange={(e: any) => handleChangeWMParams({ type: e.value })}
                            isSearchable={false}
                        />
                    </div>
                    <div className="max-w-[228px]">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateRangePicker
                                format="DD/MM/YYYY"
                                className="date-range-picker w-full"
                                onChange={(e: any) => handleDateChange(e)}
                            />
                        </LocalizationProvider>
                    </div>
                </div>
            </div>
            <RenderChart
                waterMonitoring={waterMonitoring || []}
                chartType={chartType}
                visibleParams={visibleParams}
                setVisibleParams={setVisibleParams}
                timeRange={wMParams}
                t={t}
            />
        </div>
    );
}
